import * as React from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { pageInfoTarget, type PageInfoTargetKey } from "@/components/page-info/pageInfoTarget";

interface HeaderActionIconButtonProps {
  /** Button label and accessible name. */
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  /** Render the label beside the icon instead of in a hover tooltip. */
  showLabel?: boolean;
  disabled?: boolean;
  /**
   * Native title shown while disabled. A disabled button fires no pointer
   * events, so the Radix tooltip cannot open and the reason needs the
   * attribute instead.
   */
  disabledReason?: string;
  /**
   * Defaults to the quiet `outline` every title-row control uses. Raise it only
   * for a state that is asking to be acted on rather than merely available —
   * the organise control turns `default` (brand red) once a proposal is waiting
   * to be reviewed (WHA-3654), because a finished run the user has to come back
   * to reads as nothing at all in outline.
   */
  variant?: React.ComponentProps<typeof Button>["variant"];
  /** Lets the page's ⓘ dialog point at this button ("Show me"). */
  infoTarget?: PageInfoTargetKey;
}

/**
 * Action for a page's title row (New document, New folder), the same height as
 * the other controls it sits next to. The optional visible label uses the
 * shared control scale; otherwise a tooltip keeps the compact icon-only form.
 * The glyph renders a step larger than the control scale's 12.6px.
 */
export const HeaderActionIconButton = React.forwardRef<
  HTMLButtonElement,
  HeaderActionIconButtonProps
>(function HeaderActionIconButton(
  {
    label,
    icon,
    onClick,
    showLabel = false,
    disabled,
    disabledReason,
    variant = "outline",
    infoTarget,
  },
  ref
) {
  const button = (
    <Button
      ref={ref}
      type="button"
      variant={variant}
      size={showLabel ? "control" : "controlIcon"}
      className={showLabel ? "gap-1.5 [&_svg]:size-4" : "w-auto gap-0.5 px-2 [&_svg]:size-4"}
      aria-label={label}
      title={disabled ? disabledReason : undefined}
      disabled={disabled}
      onClick={onClick}
      {...(infoTarget ? pageInfoTarget(infoTarget) : {})}
    >
      {icon}
      {showLabel && <span>{label}</span>}
    </Button>
  );

  if (showLabel) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
});
